import {Button} from '@/components/ui/button/Button'
import {DialogDismiss} from '@/components/ui/dialog-atoms/DialogAtoms'
import {useTranslation} from '@/translations/i18n'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {useFormContext, useWatch} from 'react-hook-form'
import {z} from 'zod'
import {ProjectFieldsSchema} from '@/features/projects/formSchemas'
import {useEditProjectDetails} from '@/features/projects/queries/useEditProjectDetails'
import {errorHandler, ObjectKeys} from '@/utilities/helpers'
import {useProjectMutationSuccess} from '@/features/projects/hooks/useProjectMutationSuccess'
import {QUERY_KEYS, queryClient} from '@/queryClient'
import {Spinner} from '@/components/ui/spinner/Spinner'

const EditReferringSpecialistFormFooterSchema = ProjectFieldsSchema.pick({
    id: true,
    specialist: true,
    sender: true
})
type EditReferringSpecialistFormFooterSchema = z.infer<typeof EditReferringSpecialistFormFooterSchema>

export const EditReferringSpecialistFormFooter = ({onCancel}: {onCancel: () => void}) => {
    const {t} = useTranslation()
    const form = useFormContext<EditReferringSpecialistFormFooterSchema>()
    const id = useWatch({control: form.control, name: 'id'})

    const handleSuccessProjectMutation = useProjectMutationSuccess({
        projectId: id,
        onComplete: onCancel
    })

    const {mutate: editProjectDetails, isPending} = useEditProjectDetails({
        urlParams: {projectId: id},
        options: {
            onSuccess: async () => {
                await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECT_VALIDATORS, id]})
                await handleSuccessProjectMutation()
            },
            onError: errorHandler
        }
    })

    const handleSubmit = form.handleSubmit(formValues => {
        editProjectDetails({
            specialistId: formValues.specialist?.id || null,
            sender: formValues.sender || null
        })
    })

    return (
        <Flexbox fullWidth align="center" justify="end" gap={2}>
            <DialogDismiss onClick={onCancel} render={<Button variant="secondary" />}>
                {t('commons:close')}
            </DialogDismiss>
            <Button
                variant="primaryNeutral"
                onClick={handleSubmit}
                disabled={isPending || ObjectKeys(form.formState.dirtyFields).length == 0}
            >
                {t('commons:save')}
                {isPending && <Spinner />}
            </Button>
        </Flexbox>
    )
}
